// 型番 → 対応モデル検索（住設 / 量販）。sections/aicon-model-match.liquid の器を埋める。
//
// 設計書 D85 と Figma「AiCON Match｜型番→対応モデル検索（Shopify埋め込みページ）」node 137-10440。
//
// Figma が定義する5状態:
//   1. 初期表示          result を hidden にしておく
//   2. 入力中（候補表示） /api/compare/channel/suggest
//   3. 結果あり          /api/compare/channel?model=...
//   4. 0件               found:false → 問い合わせ導線
//   5. 警告              warnings[]（未確認・推定 / 電圧相違）
//
// 購入導線は「同じサイト内の商品ページ」へ渡す。Shopify 埋め込みページなので
// クロスオリジンを跨がず、パラメータの引き継ぎも要らない。
// handle は backend が返す（aicon-return.js:30 / product-mapper.ts:75 と同じ規則）。

const SUGGEST_DEBOUNCE_MS = 200;
/** 候補を出し始める最小文字数。backend の suggestModels と揃える（ずれると空配列が返り続ける）。 */
const SUGGEST_MIN_LENGTH = 3;

const yen = (value) => `¥${new Intl.NumberFormat('ja-JP').format(value)}`;

/**
 * ロケール付きURLの接頭辞（例 /ja/）。Shopify が注入する window.Shopify.routes.root。
 * aicon-return.js:38 と同じ扱い。'/products/...' と直書きすると、
 * 多言語ストアやマーケット別ドメインで商品ページに飛べなくなる。
 * 取れない場合でも導線を諦めないよう '/' に落とす。
 */
const routeRoot = () =>
  (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

/** 商品ページの URL。handle は backend が product-mapper.ts の規則で作ったものをそのまま使う。 */
const productUrl = (handle) => `${routeRoot()}products/${handle}`;

/**
 * 差し込む値はすべてここを通す。
 * 型番・シリーズ名・備考は CSV 由来（嵐さんが手で編集する列）で、
 * 記号が混じりうる。innerHTML で組み立てるため必ずエスケープする。
 */
const esc = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

/**
 * 型番の素性を表すチップ列（モックアップのグレーのタグ）。
 * メーカー・シリーズ・畳数・電圧・年式・販路。値が無いものは出さない。
 */
const chipsHtml = (m) => {
  if (!m) return '';
  const chips = [
    m.maker ? { text: m.maker, brand: true } : null,
    m.series ? { text: m.series } : null,
    m.tatami ? { text: `おもに${m.tatami}畳` } : null,
    m.voltage ? { text: `${m.voltage}V` } : null,
    m.year ? { text: `${m.year}年モデル` } : null,
    m.channelLabel ? { text: m.channelLabel } : null,
  ].filter(Boolean);
  if (chips.length === 0) return '';
  return `<ul class="aicon-mm__tags">${chips
    .map(
      (c) =>
        `<li class="aicon-mm__tag${c.brand ? ' aicon-mm__tag--brand' : ''}">${esc(c.text)}</li>`
    )
    .join('')}</ul>`;
};

/**
 * L列（差分）の見出し文。backend の model-diff-text.ts が変換済みの文だけを返すため、
 * ここではエスケープして出すだけでよい（生の diff_note は API から来ない）。
 */
const headlineHtml = (diff) =>
  diff ? `<p class="aicon-mm__hit-desc">${esc(diff.headline)}</p>` : '';

/** N列（備考）。サニタイズ後に残らなければ undefined が来るので何も出さない。 */
const noteHtml = (note) => (note ? `<p class="aicon-mm__note">${esc(note)}</p>` : '');

/**
 * 能力の表記。カタログは「4.0kW」と小数第1位まで書くため、
 * 4 が「4kW」になるのを防いで桁を揃える（Figma の結果画面も 4.0kW）。
 */
const kw = (v) => `${Number(v).toFixed(1)}kW`;

/** 「パナソニック エオリア Xシリーズ／4.0kW（主に14畳）／2026年モデル／単相200V」を組み立てる。 */
const specLine = (m) => {
  const parts = [];
  const head = [m.maker, m.series].filter(Boolean).join(' ');
  if (head) parts.push(head);
  if (m.coolingKw) parts.push(m.tatami ? `${kw(m.coolingKw)}（主に${m.tatami}畳）` : kw(m.coolingKw));
  if (m.year) parts.push(`${m.year}年モデル`);
  if (m.voltage) parts.push(`単相${m.voltage}V`);
  return parts.join('／');
};

class AiconModelMatch extends HTMLElement {
  connectedCallback() {
    this.apiBase = (this.dataset.apiBase || '').replace(/\/$/, '');
    this.contactUrl = this.dataset.contactUrl || '';

    this.refs = {};
    this.querySelectorAll('[data-ref]').forEach((el) => {
      this.refs[el.dataset.ref] = el;
    });

    this.suggestTimer = null;
    this.suggestItems = [];
    this.activeIndex = -1;
    // 検索は入力のたびに走るため、古いレスポンスが新しい結果を上書きしないよう
    // 世代番号で弾く（比較表ページの stale() と同じ考え方）。
    this.generation = 0;

    this.bindTabs();
    this.bindForm();
    this.trackBarHeight();
    this.loadMakers();
    this.restoreFromUrl();
  }

  disconnectedCallback() {
    clearTimeout(this.suggestTimer);
    document.removeEventListener('click', this.onDocumentClick);
    this.barObserver?.disconnect();
    /*
     * ★ :root に入れた変数は自分で消す ★
     *   documentElement は要素が消えても残るため、片付けないと
     *   （テーマの部分更新などで）このページを抜けたあともフッターに
     *   バーぶんの余白が残る。
     */
    document.documentElement.style.removeProperty('--aicon-mm-bar-h');
  }

  /**
   * 追従バーの実高を CSS 変数 --aicon-mm-bar-h に流し込む。
   *
   * ★ 何のために要るのか ★
   *   検索パネルは画面下に貼り付く sticky バーで、結果は下の
   *   scrollIntoView({ block: 'nearest' }) で寄せている。ブラウザは
   *   貼り付いた要素の存在を知らないので、避ける量は CSS の
   *   scroll-margin-bottom で教えるしかない。
   *
   * ★ 固定値では足りない ★
   *   「メーカーから選ぶ」を開くとバーは 420px 級まで伸び、
   *   **選び終えた瞬間に出る結果がシートの裏に完全に隠れる**
   *   （searchBySelection は選択後もシートを畳まない）。
   *   たたんだ高さを決め打ちすると、2本ある検索経路のうち
   *   メーカー側だけが「3つ選んだのに何も起きない」状態になる。
   *
   * ★ 失敗しても壊れない ★
   *   ResizeObserver が無い環境では何もしない。CSS 側は
   *   var(--aicon-mm-bar-h, var(--aicon-mm-bar-space)) と書いてあるので
   *   たたんだ高さの目安へ落ちる（型番検索はそれで足りる）。
   */
  trackBarHeight() {
    const bar = this.refs.bar;
    if (!bar || typeof ResizeObserver === 'undefined') return;

    const apply = () => {
      /*
       * ★ 自分ではなく documentElement に入れる（山田さんFB 2026-09-06・項目3）★
       *   バーを fixed にしたので、隠れるぶんの余白は**本文とフッターの両方**が要る。
       *   フッターはこのカスタム要素の外にあるため、`this.style` に入れた変数は届かない。
       *   :root に置けば1変数で両方に配れる。
       *
       *   ★ このページ以外には影響しない ★
       *     この変数を入れるのはこのファイルだけで、他ページでは未定義のまま。
       *     フッター側の rule は var(--aicon-mm-bar-h, 0px) なので 0 に落ちる。
       */
      document.documentElement.style.setProperty(
        '--aicon-mm-bar-h',
        `${Math.round(bar.offsetHeight)}px`,
      );
    };
    this.barObserver = new ResizeObserver(apply);
    this.barObserver.observe(bar);
    apply();
  }

  // ── タブ ──────────────────────────────────────────────
  bindTabs() {
    const { tabModel, tabMaker, panelModel, panelMaker, closeRow, closeMaker } = this.refs;
    if (!tabModel || !tabMaker) return;

    const select = (which) => {
      const isModel = which === 'model';
      tabModel.classList.toggle('aicon-mm__tab--active', isModel);
      tabMaker.classList.toggle('aicon-mm__tab--active', !isModel);
      tabModel.setAttribute('aria-selected', String(isModel));
      tabMaker.setAttribute('aria-selected', String(!isModel));
      panelModel.hidden = !isModel;
      panelMaker.hidden = isModel;
      // 「閉じる」はメーカーのシートを開いているときだけ出す。
      // 型番タブでは閉じる対象が無く、出すと押しても何も起きないボタンになる。
      if (closeRow) closeRow.hidden = isModel;
    };

    tabModel.addEventListener('click', () => select('model'));
    tabMaker.addEventListener('click', () => select('maker'));

    // ★ シートを閉じる（日高さん指示 2026-09-05）★
    //   型番タブへ戻す＝入力欄が出た状態にする。選んだメーカー・シリーズ・畳数は
    //   DOM と this.selection に残るので、タブを押し直せば続きから選べる。
    closeMaker?.addEventListener('click', () => {
      select('model');
      // ★ 焦点の移し先は入力欄ではなくタブ（2026-09-05）★
      //   閉じると closeRow が hidden になり、押していたボタンが消えて
      //   焦点が body に落ちる。読み上げが位置を見失うので移す必要はある。
      //   ただし**入力欄へ移すとソフトキーボードが開き**、シートを畳んだぶんが
      //   そのままキーボードで埋まって「閉じる」の意味が無くなる。
      //   押し直せば開き直せる「メーカーから選ぶ」タブへ返す。
      tabMaker.focus();
    });
  }

  // ── 入力とフォーム ────────────────────────────────────
  bindForm() {
    const { form, input } = this.refs;
    if (!form || !input) return;

    form.addEventListener('submit', (e) => {
      e.preventDefault();
      this.hideSuggest();
      this.search(input.value);
    });

    input.addEventListener('input', () => {
      clearTimeout(this.suggestTimer);
      this.suggestTimer = setTimeout(() => this.loadSuggest(input.value), SUGGEST_DEBOUNCE_MS);
    });

    // 候補の上下移動と Enter 確定。マウスが無い環境でも候補を使えるようにする。
    input.addEventListener('keydown', (e) => {
      if (this.refs.suggest.hidden) return;
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const delta = e.key === 'ArrowDown' ? 1 : -1;
        this.moveActive(delta);
      } else if (e.key === 'Enter' && this.activeIndex >= 0) {
        e.preventDefault();
        this.pickSuggest(this.suggestItems[this.activeIndex]);
      } else if (e.key === 'Escape') {
        this.hideSuggest();
      }
    });

    // 外側クリックで候補を閉じる。
    this.onDocumentClick = (e) => {
      if (!this.contains(e.target)) this.hideSuggest();
    };
    document.addEventListener('click', this.onDocumentClick);
  }

  /** ?model=... が付いていれば復元する（結果ページを共有できるように）。 */
  restoreFromUrl() {
    const model = new URLSearchParams(location.search).get('model');
    if (!model) return;
    this.refs.input.value = model;
    this.search(model);
  }

  // ── 候補（状態2）──────────────────────────────────────
  async loadSuggest(value) {
    const q = (value || '').trim();
    if (q.length < SUGGEST_MIN_LENGTH || !this.apiBase) {
      this.hideSuggest();
      return;
    }
    try {
      const res = await fetch(
        `${this.apiBase}/api/compare/channel/suggest?q=${encodeURIComponent(q)}`
      );
      if (!res.ok) throw new Error(`suggest ${res.status}`);
      const body = await res.json();
      this.renderSuggest(body?.data?.items || []);
    } catch {
      // 候補は補助機能。落ちても検索そのものは使えるため黙って閉じる。
      this.hideSuggest();
    }
  }

  renderSuggest(items) {
    const { suggest, suggestList } = this.refs;
    this.suggestItems = items;
    this.activeIndex = -1;

    if (items.length === 0) {
      this.hideSuggest();
      return;
    }

    suggestList.innerHTML = items
      .map(
        (m, i) => `
        <li role="option" id="aicon-mm-opt-${i}" aria-selected="false">
          <button type="button" class="aicon-mm__suggest-item" data-index="${i}">
            <span class="aicon-mm__suggest-model">${esc(m.modelNumber)}</span>
            <span class="aicon-mm__suggest-desc">${esc(specLine(m))}</span>
          </button>
        </li>`
      )
      .join('');

    suggestList.querySelectorAll('.aicon-mm__suggest-item').forEach((btn) => {
      btn.addEventListener('click', () => this.pickSuggest(items[Number(btn.dataset.index)]));
    });

    suggest.hidden = false;
    this.refs.input.setAttribute('aria-expanded', 'true');
  }

  moveActive(delta) {
    const max = this.suggestItems.length - 1;
    if (max < 0) return;
    this.activeIndex = this.activeIndex + delta;
    if (this.activeIndex < 0) this.activeIndex = max;
    if (this.activeIndex > max) this.activeIndex = 0;

    this.refs.suggestList.querySelectorAll('[role="option"]').forEach((li, i) => {
      li.setAttribute('aria-selected', String(i === this.activeIndex));
    });
    this.refs.input.setAttribute('aria-activedescendant', `aicon-mm-opt-${this.activeIndex}`);
  }

  pickSuggest(item) {
    if (!item) return;
    this.refs.input.value = item.modelNumber;
    this.hideSuggest();
    this.search(item.modelNumber);
  }

  hideSuggest() {
    this.refs.suggest.hidden = true;
    this.activeIndex = -1;
    this.refs.input.setAttribute('aria-expanded', 'false');
    this.refs.input.removeAttribute('aria-activedescendant');
  }

  // ── メーカーから選ぶ（メーカー → シリーズ → 畳数）────────
  //
  // 日高さん指示 2026-08-26「最低でもメーカー、シリーズ、畳数を選択してもらうようにしてほしい」。
  // モックアップ（AiCONMatch 開発仕様 §1 の2モード目）の 1→2→3 の順送り。
  //
  // ★ 選び直しに強くする ★
  //   メーカーを選び直したらシリーズ・畳数は必ず捨てる。残すと
  //   「ダイキンの X系 で 6畳」を選んだあとメーカーだけ三菱に変えたときに、
  //   存在しない組み合わせのまま検索してしまう。

  /** いま選ばれているもの。3つ揃った時点で検索する。 */
  get selection() {
    this._selection = this._selection || { maker: null, series: null, seriesLabel: '', tatami: null };
    return this._selection;
  }

  /**
   * メーカー名 → ロゴ画像の URL。
   * セクションが data-maker-logos に JSON で渡す（asset_url は Liquid でしか引けない）。
   * 壊れた値でも UI を止めない（ロゴ無しで名前だけ出る）。
   */
  get makerLogos() {
    if (this._makerLogos) return this._makerLogos;
    try {
      this._makerLogos = JSON.parse(this.dataset.makerLogos || '{}');
    } catch {
      this._makerLogos = {};
    }
    return this._makerLogos;
  }

  /**
   * チップの並び。押されているものだけ --active を付ける。
   *
   * `it.logo` があればラベルの上にロゴを出す（メーカーの段だけ）。
   * alt は空にする。すぐ下に同じメーカー名がラベルとして出ており、
   * 読み上げが二重になるため。
   */
  renderChips(listEl, items, activeValue) {
    listEl.innerHTML = items
      .map(
        (it) => `
        <li>
          <button type="button"
            class="aicon-mm__chip${String(it.value) === String(activeValue) ? ' aicon-mm__chip--active' : ''}"
            data-value="${esc(it.value)}"
            aria-pressed="${String(it.value) === String(activeValue)}">
            ${it.logo ? `<img class="aicon-mm__chip-logo" src="${esc(it.logo)}" alt="" loading="lazy" width="116" height="20">` : ''}
            <span class="aicon-mm__chip-label">${esc(it.label)}</span>
            ${it.note ? `<span class="aicon-mm__chip-note">${esc(it.note)}</span>` : ''}
          </button>
        </li>`
      )
      .join('');
  }

  /** メーカーの一覧をチップの項目に変換する（ロゴ付き） */
  makerChipItems(makers) {
    return (makers || []).map((m) => ({
      value: m.maker,
      label: m.maker,
      note: `${m.count}機種`,
      logo: this.makerLogos[m.maker] || '',
    }));
  }

  async loadMakers() {
    if (!this.apiBase || !this.refs.makers) return;
    try {
      const res = await fetch(`${this.apiBase}/api/compare/channel/makers`);
      if (!res.ok) throw new Error(`makers ${res.status}`);
      const body = await res.json();
      const makers = body?.data?.makers || [];
      if (makers.length === 0) return;

      this.renderChips(this.refs.makers, this.makerChipItems(makers), null);
      this.refs.makers.querySelectorAll('.aicon-mm__chip').forEach((btn) => {
        btn.addEventListener('click', () => this.pickMaker(btn.dataset.value, makers));
      });

      // 入力欄の補足に出す件数を実データで上書きする（文言だけ古くなるのを防ぐ）。
      // 置き場所は「掲載データについて」→「入力欄の補足」へ移した（2026-09-01）。
      // 取得できなければ Liquid の既定値のままにする（下の catch）。
      if (this.refs.hintCount) {
        const total = makers.reduce((sum, m) => sum + m.count, 0);
        this.refs.hintCount.textContent =
          `${makers.length}メーカー${new Intl.NumberFormat('ja-JP').format(total)}機種に対応。`;
      }
    } catch {
      // 取得できなければ Liquid の既定値のまま表示する（fail-soft）。
    }
  }

  async pickMaker(maker, makers) {
    const sel = this.selection;
    sel.maker = maker;
    sel.series = null;
    sel.seriesLabel = '';
    sel.tatami = null;

    // ★ 世代番号で古い応答を弾く（searchBySelection / search と同じ仕組み）★
    //   await fetch のあとに無条件で DOM を書くと、メーカーを素早く切り替えたときに
    //   先に選んだメーカーの応答が後から届いて一覧を上書きする。
    //   sel.maker は新しいまま・チップは古いままになり、押すと
    //   maker と series が食い違った組み合わせで問い合わせて0件になる。
    const gen = ++this.generation;
    const stale = () => gen !== this.generation;

    this.renderChips(this.refs.makers, this.makerChipItems(makers), maker);
    this.refs.makers.querySelectorAll('.aicon-mm__chip').forEach((btn) => {
      btn.addEventListener('click', () => this.pickMaker(btn.dataset.value, makers));
    });

    // 下流は必ず畳む。前の選択が残ったまま見えていると、押せてしまう。
    this.refs.pickTatami.hidden = true;
    this.refs.result.hidden = true;
    this.refs.pickSeries.hidden = false;
    this.refs.seriesList.innerHTML = `<li class="aicon-mm__chips-loading">読み込んでいます…</li>`;

    try {
      const res = await fetch(
        `${this.apiBase}/api/compare/channel/series?maker=${encodeURIComponent(maker)}`
      );
      if (!res.ok) throw new Error(`series ${res.status}`);
      const body = await res.json();
      if (stale()) return;
      const series = body?.data?.series || [];
      if (series.length === 0) {
        this.refs.seriesList.innerHTML = `<li class="aicon-mm__chips-loading">このメーカーのシリーズが取得できませんでした。</li>`;
        return;
      }
      this.renderChips(
        this.refs.seriesList,
        series.map((g) => ({
          value: g.key,
          label: g.label,
          // モックアップの「対応: W・WY(ヤマダデンキ)・ME(エディオン)」。
          note: g.members.length > 0 ? `対応: ${g.members.join('・')}` : '',
        })),
        null
      );
      this.refs.seriesList.querySelectorAll('.aicon-mm__chip').forEach((btn) => {
        btn.addEventListener('click', () => this.pickSeries(btn.dataset.value, series));
      });
    } catch {
      if (stale()) return;
      this.refs.seriesList.innerHTML = `<li class="aicon-mm__chips-loading">シリーズを取得できませんでした。</li>`;
    }
  }

  async pickSeries(key, series) {
    const sel = this.selection;
    sel.series = key;
    sel.seriesLabel = (series || []).find((g) => g.key === key)?.label || key;
    sel.tatami = null;

    // pickMaker と同じ理由の世代チェック。シリーズを素早く押し替えたときに
    // 前のシリーズの畳数一覧が後から届いて上書きするのを防ぐ。
    const gen = ++this.generation;
    const stale = () => gen !== this.generation;

    this.renderChips(
      this.refs.seriesList,
      (series || []).map((g) => ({
        value: g.key,
        label: g.label,
        note: g.members.length > 0 ? `対応: ${g.members.join('・')}` : '',
      })),
      key
    );
    this.refs.seriesList.querySelectorAll('.aicon-mm__chip').forEach((btn) => {
      btn.addEventListener('click', () => this.pickSeries(btn.dataset.value, series));
    });

    this.refs.result.hidden = true;
    this.refs.pickTatami.hidden = false;
    this.refs.tatamiList.innerHTML = `<li class="aicon-mm__chips-loading">読み込んでいます…</li>`;

    try {
      const url =
        `${this.apiBase}/api/compare/channel/tatami` +
        `?maker=${encodeURIComponent(sel.maker)}&series=${encodeURIComponent(key)}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error(`tatami ${res.status}`);
      const body = await res.json();
      if (stale()) return;
      const list = body?.data?.tatami || [];
      if (list.length === 0) {
        this.refs.tatamiList.innerHTML = `<li class="aicon-mm__chips-loading">このシリーズは畳数が登録されていません。型番でお調べください。</li>`;
        return;
      }
      this.renderChips(
        this.refs.tatamiList,
        list.map((n) => ({ value: n, label: `${n}畳` })),
        null
      );
      this.refs.tatamiList.querySelectorAll('.aicon-mm__chip').forEach((btn) => {
        btn.addEventListener('click', () => this.pickTatami(Number(btn.dataset.value), list));
      });
    } catch {
      if (stale()) return;
      this.refs.tatamiList.innerHTML = `<li class="aicon-mm__chips-loading">畳数を取得できませんでした。</li>`;
    }
  }

  pickTatami(tatami, list) {
    const sel = this.selection;
    sel.tatami = tatami;
    this.renderChips(
      this.refs.tatamiList,
      (list || []).map((n) => ({ value: n, label: `${n}畳` })),
      tatami
    );
    this.refs.tatamiList.querySelectorAll('.aicon-mm__chip').forEach((btn) => {
      btn.addEventListener('click', () => this.pickTatami(Number(btn.dataset.value), list));
    });
    this.searchBySelection();
  }

  /** 3つ揃ったら検索する。型番検索と同じ描画関数へ渡す。 */
  async searchBySelection() {
    const { maker, series, tatami } = this.selection;
    if (!maker || !series || !tatami || !this.apiBase) return;

    const gen = ++this.generation;
    const stale = () => gen !== this.generation;
    const { result } = this.refs;

    result.hidden = false;
    result.innerHTML = `<div class="aicon-mm__card"><p>検索しています…</p></div>`;

    try {
      const url =
        `${this.apiBase}/api/compare/channel` +
        `?maker=${encodeURIComponent(maker)}&series=${encodeURIComponent(series)}&tatami=${tatami}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error(`selection ${res.status}`);
      const body = await res.json();
      if (stale()) return;
      const data = body?.data;
      if (!data || !data.found) {
        this.renderEmpty(`${maker} ${this.selection.seriesLabel} ${tatami}畳`, data?.reason);
        return;
      }
      this.renderResult(data);
      // 結果まで一気にスクロールしない（選び直しの導線を画面内に残す）。
      result.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    } catch {
      if (stale()) return;
      result.innerHTML = `
        <div class="aicon-mm__card">
          <p class="aicon-mm__empty-title">検索できませんでした</p>
          <p class="aicon-mm__empty-body">通信状況をご確認のうえ、もう一度お試しください。</p>
        </div>`;
    }
  }

  // ── 検索（状態3/4/5）──────────────────────────────────
  async search(value) {
    const model = (value || '').trim();
    const { result, submit } = this.refs;
    if (model.length === 0 || !this.apiBase) return;

    const gen = ++this.generation;
    const stale = () => gen !== this.generation;

    submit.disabled = true;
    result.hidden = false;
    result.innerHTML = `<div class="aicon-mm__card"><p>検索しています…</p></div>`;

    try {
      const res = await fetch(
        `${this.apiBase}/api/compare/channel?model=${encodeURIComponent(model)}`
      );
      if (!res.ok) throw new Error(`channel ${res.status}`);
      const body = await res.json();
      if (stale()) return;

      const data = body?.data;
      if (!data || !data.found) {
        this.renderEmpty(model, data?.reason);
        return;
      }
      this.renderResult(data);
    } catch {
      if (stale()) return;
      // 通信エラーは 0件とは別物。同じ画面にすると「掲載が無い」と誤解される。
      result.innerHTML = `
        <div class="aicon-mm__card">
          <p class="aicon-mm__empty-title">検索できませんでした</p>
          <p class="aicon-mm__empty-body">通信状況をご確認のうえ、もう一度お試しください。</p>
        </div>`;
    } finally {
      if (!stale()) submit.disabled = false;
    }
  }

  /** 状態4: 0件（問い合わせ導線）。 */
  renderEmpty(model, reason) {
    const cta = this.contactUrl
      ? `<a class="aicon-mm__empty-cta" href="${esc(this.contactUrl)}">型番を送って問い合わせる</a>`
      : '';
    this.refs.result.innerHTML = `
      <div class="aicon-mm__card">
        <p class="aicon-mm__empty-title">
          <span class="aicon-mm__empty-chip">0件</span>対応するモデルは見つかりませんでした
        </p>
        <p class="aicon-mm__empty-body">${esc(reason || `「${model}」に一致する型番が見つかりませんでした。`)}</p>
        ${cta}
      </div>`;
  }

  /** 状態3（＋状態5の警告）。 */
  renderResult(data) {
    const parts = [];

    // 警告を最初に出す。価格を見てから気づくのでは遅い。
    if (data.warnings?.length) {
      parts.push(data.warnings.map((w) => this.warningHtml(w)).join(''));
    }

    // お調べの型番
    if (data.queriedModel) {
      parts.push(`
        <div class="aicon-mm__queried">
          <p class="aicon-mm__queried-label">お調べの型番<span class="aicon-mm__queried-model">${esc(data.queriedModel.modelNumber)}</span></p>
          <p class="aicon-mm__queried-spec">${esc(specLine(data.queriedModel))}</p>
          ${chipsHtml(data.queriedModel)}
          ${noteHtml(data.queriedModel.note)}
        </div>`);
    }

    // 同じ基本仕様の住宅設備用モデル（＋仕様比較表）
    parts.push(this.jusetsuHtml(data.jusetsu, data.queriedModel, data.specs));

    // 住設モデルと量販店モデルの違い
    parts.push(this.diffHtml(data));

    // 対応表
    if (data.table?.length) parts.push(this.tableHtml(data.table));

    this.refs.result.innerHTML = parts.join('');
  }

  warningHtml(w) {
    const chip = w.kind === 'unconfirmed' ? '未確認・推定' : '電圧の相違';
    return `
      <div class="aicon-mm__warn aicon-mm__warn--${esc(w.kind)}">
        <p class="aicon-mm__warn-title"><span class="aicon-mm__warn-chip">${chip}</span>${esc(w.title)}</p>
        <p class="aicon-mm__warn-body">${esc(w.body)}</p>
      </div>`;
  }

  jusetsuHtml(j, queried, specs) {
    // 住設対応が無い帯がある（運用ルール「住設フィルタで0件＝その帯は住設対応なし」）。
    // その場合は対応表だけを見せ、量販行を実質最安として案内する。
    if (!j) {
      return `
        <div class="aicon-mm__card">
          <p class="aicon-mm__card-title">同じ基本仕様の住宅設備用モデル</p>
          <p class="aicon-mm__empty-body">この帯には住宅設備用モデルの設定がありません。下の対応表から、流通ルートごとの型番をご確認ください。</p>
        </div>`;
    }

    // ★ 出すのは本体価格（日高さん指示 2026-09-01）★
    //   以前は本体＋標準取付工事の総額（totalPrice）を「標準取付工事費込み・税込」
    //   として出していた。ここで工事費を足すと
    //     ・商品ページ・一覧・広告（Merchant Center）とこの画面だけ額が違う
    //       ── 商品価格に工事費を含めない、はストア全体の決めごと（shopify/README.md）
    //     ・工事の前提（取替え/新規・能力帯）が画面に書けないため、どの条件の額か伝わらない
    //   となる。∴ 本体価格だけを出し、工事費は商品ページの内訳に任せる。
    //   backend は unitPrice / installationFee / totalPrice をすべて返しているので、
    //   出す値を選び直すだけで済む（API は変えていない）。
    const price = j.available
      ? `
        <div class="aicon-mm__price">
          <span class="aicon-mm__price-tag">最安挑戦</span>
          <span class="aicon-mm__price-value">${yen(j.unitPrice)}</span>
          <span class="aicon-mm__price-note">本体価格・税込</span>
        </div>
        <a class="aicon-mm__cta" href="${esc(productUrl(j.handle))}">${esc(j.modelNumber)} の商品ページを見る</a>
        <p class="aicon-mm__cta-note">取付工事費は別途かかります。工事内容・地域により変わるため、詳細ページでご確認ください。</p>`
      : `<p class="aicon-mm__cta-note">この型番は現在当店での取り扱いがありません。下の対応表をご確認ください。</p>`;

    return `
      <div class="aicon-mm__card">
        <p class="aicon-mm__lede">同じ基本仕様で、当店ではこちらを取り扱っています</p>
        <p class="aicon-mm__hit-model">${esc(j.modelNumber)}</p>
        ${chipsHtml(j)}
        ${headlineHtml(queried?.diff)}
        ${this.specsHtml(specs, queried, j)}
        ${noteHtml(j.note)}
        ${price}
      </div>`;
  }

  /**
   * 仕様比較表（モックアップの「お調べの型番 ⇄ こちらの商品」）。
   *
   * ★ 数字と単位は backend が作った文字列をそのまま出す ★
   *   ここで単位を足すと、項目を増やすたびにテーマ JS も直すことになる。
   *   一致判定（緑字）も backend の match フラグに従う。JS 側で再判定すると
   *   「2.8」と「2.8 kW」のような書式差で判定が割れる。
   */
  specsHtml(specs, queried, jusetsu) {
    if (!specs || specs.length === 0) return '';
    const rows = specs
      .map(
        (r) => `
        <tr class="${r.match ? 'aicon-mm__spec-row--match' : ''}">
          <th scope="row">${esc(r.label)}</th>
          <td>${esc(r.queried ?? '―')}</td>
          <td>${esc(r.jusetsu ?? '―')}</td>
        </tr>`
      )
      .join('');

    return `
      <div class="aicon-mm__spec-wrap">
        <table class="aicon-mm__spec">
          <thead>
            <tr>
              <th scope="col"><span class="aicon-mm__sr">項目</span></th>
              <th scope="col">お調べの型番<small>${esc(queried?.modelNumber ?? '')}</small></th>
              <th scope="col">こちらの商品<small>${esc(jusetsu?.modelNumber ?? '')}</small></th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
      </div>
      <p class="aicon-mm__spec-foot">緑字＝メーカー公式仕様表で数値が一致している項目。対応関係は当社調べです。</p>`;
  }

  /**
   * 「住宅設備用モデルと量販店モデルの違い」。
   *
   * 日高さん指示 2026-08-26「L列・N列の内容をもとに量販店モデルと住設モデルの違いを説明したい」。
   * 固定の説明文はセクション設定（data-explainer）から、機種ごとの違いは
   * L列（変換済み diff）と N列（サニタイズ済み note）から作る。
   */
  diffHtml(data) {
    const q = data.queriedModel;
    const j = data.jusetsu;
    const diff = q?.diff;
    const explainer = this.dataset.explainer || '';

    // 出す材料が何も無いなら枠ごと出さない。
    if (!explainer && !diff?.onlyThis && !diff?.onlyOther && !q?.note && !j?.note) return '';
    const sides = [];
    if (diff?.onlyThis) {
      sides.push(`
        <li class="aicon-mm__side">
          <p class="aicon-mm__side-label">お調べの型番（${esc(q?.channelLabel ?? '')}）にだけあるもの</p>
          <p class="aicon-mm__side-body">${esc(diff.onlyThis)}</p>
        </li>`);
    }
    if (diff?.onlyOther) {
      sides.push(`
        <li class="aicon-mm__side aicon-mm__side--jusetsu">
          <p class="aicon-mm__side-label">当店の住宅設備用モデルにだけあるもの</p>
          <p class="aicon-mm__side-body">${esc(diff.onlyOther)}</p>
        </li>`);
    }

    const notes = [
      q?.note ? [`お調べの型番（${q.modelNumber}）`, q.note] : null,
      j?.note ? [`当店の住宅設備用モデル（${j.modelNumber}）`, j.note] : null,
    ].filter(Boolean);

    return `
      <div class="aicon-mm__card">
        <p class="aicon-mm__card-title">住宅設備用モデルと量販店モデルの違い</p>
        ${explainer ? `<p class="aicon-mm__diff-lead">${esc(explainer)}</p>` : ''}
        ${
          // 見出し文（diff.headline）は住設カード側で既に出している。
          // ここで繰り返すと同じ一文が画面に2回並ぶので、片側だけの装備がある
          // ときにだけ、その導入として出す。
          sides.length
            ? `<p class="aicon-mm__diff-headline aicon-mm__diff-headline--${esc(diff.kind)}">${esc(diff.headline)}</p>`
            : ''
        }
        ${sides.length ? `<ul class="aicon-mm__sides">${sides.join('')}</ul>` : ''}
        ${
          notes.length
            ? `<dl class="aicon-mm__notes">${notes
                .map(
                  ([label, body]) =>
                    `<dt>${esc(label)}の補足</dt><dd>${esc(body)}</dd>`
                )
                .join('')}</dl>`
            : ''
        }
      </div>`;
  }

  tableHtml(rows) {
    const items = rows
      .map((r) => {
        const badges = [
          `<span class="aicon-mm__badge ${r.channel === '住設' ? '' : 'aicon-mm__badge--other'}">${esc(r.channelLabel)}</span>`,
          r.isInput ? `<span class="aicon-mm__badge aicon-mm__badge--input">お調べの型番</span>` : '',
        ].join('');

        // 住設カードと同じく本体価格を出す（同じ画面で2種類の額を並べない）
        const price =
          r.available && r.unitPrice
            ? `<span class="aicon-mm__row-price">${yen(r.unitPrice)} <small>本体価格・税込</small></span>
               <a class="aicon-mm__row-cta" href="${esc(productUrl(r.handle))}">購入ページへ</a>`
            : '';

        // L列・N列は行ごとに出す（日高さん指示 2026-08-26「全て反映して」）。
        // 記載が無い行は何も出さない。
        const detail = [
          r.diff && r.diff.kind !== 'unknown'
            ? `<p class="aicon-mm__row-diff">${esc(r.diff.headline)}</p>`
            : '',
          r.diff?.onlyThis
            ? `<p class="aicon-mm__row-diff">この型番のみ：${esc(r.diff.onlyThis)}</p>`
            : '',
          r.diff?.onlyOther
            ? `<p class="aicon-mm__row-diff">ベース（住宅設備用）のみ：${esc(r.diff.onlyOther)}</p>`
            : '',
          r.note ? `<p class="aicon-mm__row-note-extra">${esc(r.note)}</p>` : '',
        ].join('');

        return `
          <li class="aicon-mm__row ${r.channel === '住設' ? 'aicon-mm__row--jusetsu' : ''}">
            <div class="aicon-mm__row-head">
              <span>${badges}</span>
              <span class="aicon-mm__row-model">${esc(r.modelNumber)}</span>
              ${r.store ? `<span class="aicon-mm__row-note">${esc(r.store)}</span>` : ''}
              ${price}
            </div>
            ${detail ? `<div class="aicon-mm__row-detail">${detail}</div>` : ''}
          </li>`;
      })
      .join('');

    return `
      <div class="aicon-mm__card">
        <p class="aicon-mm__table-title">対応表</p>
        <p class="aicon-mm__table-lead">同じ基本仕様で、流通ルートごとに型番が分かれています。ベース（住宅設備用）との違いと補足を、記載があるものだけ添えています。</p>
        <ul class="aicon-mm__rows">${items}</ul>
        <p class="aicon-mm__table-foot">当店で取り扱いがあるのは住宅設備用モデルです。それ以外の型番は対応関係の参考情報として掲載しています。</p>
      </div>`;
  }
}

if (!customElements.get('aicon-model-match')) {
  customElements.define('aicon-model-match', AiconModelMatch);
}
